import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useFetchProductDetailsQuery } from "../../../api/products/hooks/useFetchProductDetails";
import { useProductCart } from "../../../atoms/products/hooks/useProductCart";
import { useIsVisitingOwnProfile } from "../../../hooks/profileScreenHooks/useIsVisitingOwnProfile";
import { useMediaQueryBreakpoint } from "../../../hooks/useMediaQuery";
import { ReleaseTrack } from "../../../store/models/release";
import { getMyMusicRoute } from "../../../store/utils/routeGetters";
import { isLiveSimple } from "../../../utils/releases/dateUtils";
import { ButtonVariant } from "../../core-ui/components/Button/Button";
import { FixedMinWidthButton } from "../../core-ui/components/Button/FixedMinWidthButton";
import { MyMusicTabs } from "../../screens/MyMusic/constants";

interface ProductModalFooterButtonsProps {
  productPurchasedByCurrentUser: boolean;
  productId: number;
  releaseTracks: ReleaseTrack[];
  productsIds: number[];
  availableQuantity?: number | null;
  purchaseLimit?: number | null;
  productName: string;
}

export const ProductModalFooterButtons = ({
  productPurchasedByCurrentUser,
  productId,
  releaseTracks,
  productsIds,
  availableQuantity,
  purchaseLimit,
  productName,
}: ProductModalFooterButtonsProps) => {
  const navigate = useNavigate();
  const {
    cart,
    handleCheckout,
    isCheckingout,
    handleReplaceBundle,
    handleRemoveBundle,
  } = useProductCart();
  const { data: product } = useFetchProductDetailsQuery(productId);
  const { isMobile } = useMediaQueryBreakpoint();
  const isVisitingOwnProfile = useIsVisitingOwnProfile();

  const isLive = useMemo(() => {
    if (!product) return false;
    const publishedAt = product.published_at || null;
    const publishedEnd = product.published_end || null;
    return isLiveSimple(publishedAt, publishedEnd);
  }, [product]);

  if (productPurchasedByCurrentUser) {
    return (
      <FixedMinWidthButton
        variant={ButtonVariant.OUTLINED}
        onClick={() => {
          navigate(getMyMusicRoute(MyMusicTabs.Library));
        }}
      >
        My purchases
      </FixedMinWidthButton>
    );
  }

  const productInCart = cart.find((item) => item.product_id === productId);
  return (
    <>
      <FixedMinWidthButton
        variant={ButtonVariant.OUTLINED}
        minWidth={isMobile ? "120px" : undefined}
        onClick={() => {
          return productInCart
            ? handleRemoveBundle(productId)
            : handleReplaceBundle(productId, releaseTracks, productsIds);
        }}
        disabled={isVisitingOwnProfile || !isLive}
      >
        {productInCart ? "Remove from Cart" : "Add to cart"}
      </FixedMinWidthButton>
      <FixedMinWidthButton
        minWidth={isMobile ? "120px" : undefined}
        onClick={() => {
          let updatedCart = cart;
          if (!productInCart) {
            if (purchaseLimit && availableQuantity === 0) {
              toast.error(`${productName}  is out of stock`);
              return;
            }
            updatedCart = handleReplaceBundle(
              productId,
              releaseTracks,
              productsIds,
            );
          }

          void handleCheckout(updatedCart);
        }}
        loading={isCheckingout}
        disabled={isVisitingOwnProfile || !isLive}
      >
        {isMobile ? "Checkout" : "Checkout"}
      </FixedMinWidthButton>
    </>
  );
};
